import { defaultServerUrl } from "./storage.js";
/** Resolve a server path (like /media/ABCD/x.mp4) against the game server; absolute URLs pass through. */
export function serverUrl(pathOrUrl) {
    return new URL(pathOrUrl, defaultServerUrl()).toString();
}
/** The best recording format this browser supports. iPhones prefer MP4; Android and Chrome prefer WebM. */
export function pickRecorderType(kind) {
    const candidates = kind === "video"
        ? ["video/mp4;codecs=avc1.42E01E,mp4a.40.2", "video/mp4", "video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm"]
        : ["audio/mp4", "audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus"];
    const MR = globalThis.MediaRecorder;
    return candidates.find((t) => MR?.isTypeSupported?.(t)) ?? "";
}
/**
 * PUT a file with upload progress (fetch can't report upload progress yet).
 * Resolves on 2xx; rejects with a friendly message otherwise.
 */
export function uploadWithProgress(url, body, headers, onProgress, timeoutMs = 120_000) {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", serverUrl(url));
        for (const [k, v] of Object.entries(headers))
            xhr.setRequestHeader(k, v);
        xhr.timeout = timeoutMs;
        xhr.upload.onprogress = (e) => {
            if (e.lengthComputable)
                onProgress(e.loaded / e.total);
        };
        xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(uploadError(xhr.status))));
        xhr.onerror = () => reject(new Error("The upload failed. Check your connection and try again."));
        xhr.ontimeout = () => reject(new Error("The upload took too long. Try again on better Wi-Fi."));
        xhr.send(body);
    });
}
function uploadError(status) {
    if (status === 413)
        return "That diary is too large. Keep it under 15 seconds.";
    if (status === 403)
        return "That upload link expired. Try sending again.";
    return `The upload failed (${status}). Try again.`;
}
let reportContext = {};
export function setReportContext(ctx) {
    reportContext = { ...reportContext, ...ctx };
}
/** A short description of the device: enough to spot "it's always the Samsung". */
export function deviceSummary() {
    try {
        const ua = navigator.userAgent;
        const model = /Android [\d.]+; ([^;)]+)/.exec(ua)?.[1];
        const os = /iPhone|iPad/.test(ua)
            ? `iOS ${/OS (\d+[_\d]*)/.exec(ua)?.[1]?.replace(/_/g, ".") ?? ""}`
            : /Android [\d.]+/.exec(ua)?.[0] ?? (/Tizen/.test(ua) ? "Samsung TV" : /Mac OS X/.test(ua) ? "Mac" : /Windows/.test(ua) ? "Windows" : "Other");
        const safari = /Version\/[\d.]+.*Safari/.test(ua) ? "Safari" : "";
        const browser = /SamsungBrowser\/[\d.]+/.exec(ua)?.[0] ?? /CriOS|Chrome\/\d+/.exec(ua)?.[0] ?? /FxiOS|Firefox\/\d+/.exec(ua)?.[0] ?? safari;
        return [os, model, browser, `${screen.width}x${screen.height}`].filter(Boolean).join(", ").slice(0, 300);
    }
    catch {
        return "";
    }
}
function post(path, body) {
    try {
        return fetch(serverUrl(path), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ ...reportContext, device: deviceSummary(), ...body }),
            keepalive: true,
        })
            .then((r) => r.ok)
            .catch(() => false);
    }
    catch {
        return Promise.resolve(false); /* never let error reporting cause an error */
    }
}
/** Report a problem the app caught itself (a diary that wouldn't play, say). */
export function reportProblem(app, message, detail) {
    void post("/api/client-error", { message, stack: detail, app, url: location.pathname });
}
/** Send uncaught browser errors to the server (and on to Sentry when configured). */
export function reportErrors(app) {
    let sent = 0;
    const send = (message, stack) => {
        if (++sent > 20)
            return; // one broken loop shouldn't flood the list
        void post("/api/client-error", { message, stack, app, url: location.pathname });
    };
    window.addEventListener("error", (e) => send(e.message, e.error?.stack));
    window.addEventListener("unhandledrejection", (e) => send(String(e.reason?.message ?? e.reason), e.reason?.stack));
}
/** A tester's rating or "Report a problem", or a connection drop. Resolves true if it arrived. */
export function sendFeedback(report) {
    return post("/api/feedback", { ...report });
}
/** Feature switches that are on for this viewer. Empty if the server can't be reached. */
export async function fetchFlags() {
    try {
        const res = await fetch(serverUrl("/api/flags"), { credentials: "same-origin" });
        if (!res.ok)
            return new Set();
        const json = (await res.json());
        return new Set(json.flags ?? []);
    }
    catch {
        return new Set();
    }
}
//# sourceMappingURL=media.js.map